/**
 * @file stock-placement-error.ts
 * @description Разбор ошибки проведения размещения (phase19.24 этап 1, шаг 4;
 * выделено из `stock-placement-texts.ts` по ревью 2026-09-20).
 *
 * Ошибки приходят кодами реестра перемещений (422) и `lot_moved` (409).
 * Человеческий текст берёт общий `wmsErrorText`, здесь решаются два других
 * вопроса: к какой ИМЕННО партии относится жалоба (её показываем строкой в
 * шторке, а не общим тостом) и что стало с документом.
 *
 * Формат `details` у нового эндпоинта может отличаться от известных, поэтому
 * разбор защитный: не нашли привязки к партии — остаётся общий текст.
 */

import { PLACEMENT_TEXT } from "./stock-placement-texts";
import { ApiError } from "../../../lib/api-client";
import { wmsErrorText } from "../../../lib/wms-error-text";

/** Разбор ошибки размещения: общий текст плюс жалобы по конкретным партиям. */
export interface PlacementError {
  /** Текст для тоста и полосы вверху шторки. */
  message: string;
  /** `lotId` → что не так именно с этой партией. */
  byLot: Record<string, string>;
  /** Черновик, оставшийся после упавшего проведения; `null` — документа нет. */
  transferId: string | null;
  /** Его номер (`MV-…`) для полосы; `null` — сервер номер не прислал. */
  transferNumber: string | null;
  /** Полоса «документ остался черновиком»; `null` — говорить не о чем. */
  partialNotice: string | null;
}

/** Строка деталей: сервер может назвать партию id или номером. */
interface PlacementErrorDetailItem {
  lotId?: unknown;
  batchNumber?: unknown;
  code?: unknown;
  message?: unknown;
  actualBinCode?: unknown;
}

/** Строка — только если это непустая строка; иначе поля в деталях нет. */
function str(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

/** Строки `details.items` — у всех известных кодов детали лежат именно там. */
function detailItems(details: unknown): PlacementErrorDetailItem[] {
  if (!details || typeof details !== "object") return [];
  const raw = (details as { items?: unknown }).items;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (row): row is PlacementErrorDetailItem => !!row && typeof row === "object",
  );
}

/**
 * Текст жалобы на партию: сначала то, что прислал сервер, потом известный
 * частный случай «партия уехала», иначе — общий текст ошибки.
 */
function itemText(item: PlacementErrorDetailItem, fallback: string): string {
  const own = str(item.message);
  if (own) return own;
  const actual = str(item.actualBinCode);
  if (actual) return `Партия уже лежит в ${actual} — обновите отбор`;
  return fallback;
}

/**
 * Ошибка проведения → что показать: общий текст, разметка проблемных строк и
 * ссылка на оставшийся черновик.
 *
 * Атомарности у проведения НЕТ (находка ревью 2026-09-20): упав на строке,
 * сервер оставляет документ черновиком и кладёт его в `details.transferId` /
 * `details.transferNumber`. Промолчать значит бросить кладовщика с висящим
 * MV-документом, о котором он не знает.
 */
export function parsePlacementError(err: unknown): PlacementError {
  const message = wmsErrorText(err, PLACEMENT_TEXT.placementError);
  const empty = {
    message,
    byLot: {},
    transferId: null,
    transferNumber: null,
    partialNotice: null,
  };
  if (!(err instanceof ApiError)) return empty;
  const byLot: Record<string, string> = {};
  for (const item of detailItems(err.details)) {
    const lotId = str(item.lotId);
    if (!lotId) continue;
    byLot[lotId] = itemText(item, message);
  }
  const draft = placementDraftRef(err.details);
  return {
    message,
    byLot,
    ...draft,
    partialNotice: draft.transferId
      ? draft.transferNumber
        ? PLACEMENT_TEXT.placementPartial(draft.transferNumber)
        : PLACEMENT_TEXT.placementPartialNoNumber
      : null,
  };
}

/** Черновик из `details` упавшего проведения: id и человеческий номер. */
function placementDraftRef(details: unknown): {
  transferId: string | null;
  transferNumber: string | null;
} {
  if (!details || typeof details !== "object") {
    return { transferId: null, transferNumber: null };
  }
  const raw = details as { transferId?: unknown; transferNumber?: unknown };
  return {
    transferId: str(raw.transferId),
    transferNumber: str(raw.transferNumber),
  };
}
